import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, NgModule } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import * as i2 from '@angular/common/http';
import { HttpResponse, HttpClientModule } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import * as i2$1 from '@usana/ux/ssr';

class SsoUser {
    static fromJSON(json) {
        try {
            return JSON.parse(json);
        }
        catch (error) {
            return null;
        }
    }
}

class SsoAuthService {
    constructor() {
        this.userSubject = new ReplaySubject(1);
    }
    getUserSubject() {
        return this.userSubject;
    }
    getUserUnchecked() {
        return this.currentUser;
    }
    getIdTokenUnchecked() {
        return this.currentIdToken;
    }
    clearCurrent() {
        this.setUserAndToken(null, null);
    }
    setUserFromIdToken(idToken) {
        if (!idToken) {
            this.clearCurrent();
            return false;
        }
        const user = this.parseIdTokenJwt(idToken);
        if (user) {
            // we have a non expired token
            this.setUserAndToken(user, idToken);
            return true;
        }
        return false;
    }
    // call if there is no user when the app is first loaded.
    initNoUser() {
        this.clearCurrent(); // set no user
        this.userSubject.next(null); // first value is null
    }
    parseIdTokenJwt(idToken) {
        if (!!idToken) {
            try {
                const b64u = idToken.split('.')[1]; // get the payload
                const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(atob(b64)
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join(''));
                const user = SsoUser.fromJSON(jsonPayload);
                // if we haven't expired, (multiply by 1000 since its in seconds)
                if (user.exp * 1000 > new Date().getTime()) {
                    return user;
                }
            }
            catch (error) {
                // do we care?
            }
        }
        return null;
    }
    setUserAndToken(user, idToken) {
        const prevSub = this.subIfExists(this.currentUser); // get the previous value before we update it
        // these value should be immutable lets freeze them on the way in
        this.currentUser = Object.freeze(user);
        this.currentIdToken = idToken; // strings are already immutable
        if (prevSub !== this.subIfExists(this.currentUser)) {
            // no reason to push a new user subject if we are setting the same sub object.
            this.userSubject.next(this.currentUser);
        }
    }
    subIfExists(user) {
        if (!!user) {
            return user.sub;
        }
        return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class SsoInterceptExclusionService {
    constructor() {
        this.exclusions = [];
    }
    addExclusions(...exclusions) {
        this.exclusions.push(...exclusions);
    }
    isExcludedUrl(url) {
        // one or more urls
        return this.exclusions.some((rgx) => rgx.test(url));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoInterceptExclusionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoInterceptExclusionService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoInterceptExclusionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

const ID_TOKEN_NAME = new InjectionToken('sso.id_token');
const LOGIN_REALM = new InjectionToken('sso.realm');
const LOGIN_SERVICE_URL = new InjectionToken('sso.service.url');
const LOGIN_APP_URL = new InjectionToken('sso.app.url');
const SWITCH_USER_CHANGE_MARKET = new InjectionToken('sso.switchUser.changeMarket');

class SsoLogoutService {
    constructor(ssoAuthService, httpClient, loginRealm, loginServiceUrl) {
        this.ssoAuthService = ssoAuthService;
        this.httpClient = httpClient;
        this.loginRealm = loginRealm;
        this.loginServiceUrl = loginServiceUrl;
        this.preCallbacks = [];
        this.postCallbacks = [];
    }
    registerPreLogoutCallback(preCallback) {
        this.preCallbacks.push(preCallback);
    }
    deregisterPreLogoutCallback(cbUid) {
        return this.removeFromArrayWithUid(this.preCallbacks, cbUid);
    }
    registerPostLogoutCallback(postCallback) {
        this.postCallbacks.push(postCallback);
    }
    deregisterPostLogoutCallback(cbUid) {
        return this.removeFromArrayWithUid(this.postCallbacks, cbUid);
    }
    logout() {
        return new Promise((resolve) => {
            this.handleAllCallbacks(this.preCallbacks).then((success) => {
                this.ssoLogout().finally(() => {
                    this.ssoAuthService.clearCurrent();
                    this.handleAllCallbacks(this.postCallbacks).then(() => {
                        // finally we resolve our promise
                        resolve();
                    });
                });
            });
        });
    }
    ssoLogout() {
        return this.httpClient
            .post(`${this.loginServiceUrl}/logout/${this.loginRealm}`, null, {
            withCredentials: true,
        })
            .toPromise();
    }
    handleAllCallbacks(arr) {
        return new Promise((resolve) => {
            Promise.all(arr.map((cb) => cb.callback()))
                .then(() => {
                // all succeeded
                resolve(true);
            })
                .catch(() => {
                // one or more failed
                resolve(false);
            });
        });
    }
    removeFromArrayWithUid(arr, uid) {
        for (let i = arr.length - 1; i >= 0; --i) {
            if (arr[i].uid === uid) {
                arr.splice(i, 1);
                return true;
            }
        }
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoLogoutService, deps: [{ token: SsoAuthService }, { token: i2.HttpClient }, { token: LOGIN_REALM }, { token: LOGIN_SERVICE_URL }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoLogoutService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoLogoutService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: SsoAuthService }, { type: i2.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGIN_REALM]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGIN_SERVICE_URL]
                }] }]; } });

const SsoAuthConstants = {
    AUTH_HEADER: 'Authorization',
    BEARER: 'Bearer',
};

/** Pass untouched request through to the next request handler. */
class SsoAuthInterceptor {
    constructor(ssoAuthService, ssoInterceptExclusionService, idTokenName) {
        this.ssoAuthService = ssoAuthService;
        this.ssoInterceptExclusionService = ssoInterceptExclusionService;
        this.idTokenName = idTokenName;
    }
    intercept(req, next) {
        if (this.ssoAuthService.getIdTokenUnchecked() && !this.ssoInterceptExclusionService.isExcludedUrl(req.url)) {
            const addedHeaders = {};
            // we prefer a Bearer header if possible, but we don't want to override our Auth header if it exists
            if (!req.headers.has(SsoAuthConstants.AUTH_HEADER)) {
                addedHeaders[SsoAuthConstants.AUTH_HEADER] = `${SsoAuthConstants.BEARER} ${this.ssoAuthService.getIdTokenUnchecked()}`;
            }
            else {
                // if we already have an auth header we will pass the token as a standard header
                addedHeaders[this.idTokenName] = this.ssoAuthService.getIdTokenUnchecked();
            }
            req = req.clone({
                setHeaders: addedHeaders,
                withCredentials: true,
            });
        }
        return next.handle(req).pipe(map((event, index, 
        // we pass in idtoken here to deal with our map scope being different
        idToken = this.idTokenName) => {
            if (event instanceof HttpResponse) {
                const resp = event; // don't care about the body
                if (resp.headers.has(idToken)) {
                    this.ssoAuthService.setUserFromIdToken(resp.headers.get(idToken));
                }
            }
            return event;
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthInterceptor, deps: [{ token: SsoAuthService }, { token: SsoInterceptExclusionService }, { token: ID_TOKEN_NAME }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: SsoAuthService }, { type: SsoInterceptExclusionService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ID_TOKEN_NAME]
                }] }]; } });

class LoginResponse {
}

class LoginTokens {
}

class Interrupt {
}

class SsoLogoutCallbackDefinition {
}

class SsoAuthModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthModule, imports: [CommonModule, HttpClientModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthModule, imports: [CommonModule, HttpClientModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SsoAuthModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [CommonModule, HttpClientModule],
                    providers: [
                    // SsoAuthService, //in root
                    // interceptor should be in app
                    ],
                    exports: [],
                }]
        }] });

class BaseSsoGuard {
    constructor(ssoAuthService, locationService, loginAppUrl) {
        this.ssoAuthService = ssoAuthService;
        this.locationService = locationService;
        this.loginAppUrl = loginAppUrl;
    }
    // | UrlTree???
    loadUserAndEval(route, state) {
        return this.ssoAuthService
            .getUserSubject()
            .asObservable()
            .pipe(map((ssoUser) => {
            if (!this.isUserAllowed(ssoUser, route, state)) {
                this.navigateToLogin(this.getUrl(state));
                return false;
            }
            return true;
        }));
    }
    navigateToLogin(redirectUri) {
        const url = `${this.loginAppUrl}/login/${encodeURIComponent(redirectUri)}`;
        this.locationService.assign(url);
    }
    getUrl(state) {
        return `${this.locationService.protocol()}//${this.locationService.hostname()}${state.url}`;
    }
    canActivate(route, state) {
        return this.loadUserAndEval(route, state);
    }
    canActivateChild(route, state) {
        return this.loadUserAndEval(route, state);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BaseSsoGuard, deps: [{ token: SsoAuthService }, { token: i2$1.PlatformWrappedLocationService }, { token: LOGIN_APP_URL }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BaseSsoGuard }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BaseSsoGuard, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: SsoAuthService }, { type: i2$1.PlatformWrappedLocationService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOGIN_APP_URL]
                }] }]; } });

class AuthenticatedSsoGuard extends BaseSsoGuard {
    isUserAllowed(ssoUser, route, state) {
        return ssoUser != null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticatedSsoGuard, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticatedSsoGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AuthenticatedSsoGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class RoleSsoGuard extends BaseSsoGuard {
    isUserAllowed(ssoUser, route, state) {
        if (!route.data) {
            // no data so we can't have roles provided so we aren't allowed in
            return false;
        }
        const roles = route.data.roles;
        if (!roles || roles.length === 0) {
            // if you didn't give us any roles than we can't let you through.
            return false;
        }
        if (!ssoUser || !ssoUser.roles || ssoUser.roles.length === 0) {
            // if the user doesn't have any roles obviously it can't pass either
            return false;
        }
        return roles.some((role) => ssoUser.roles.includes(role));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RoleSsoGuard, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RoleSsoGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RoleSsoGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/*
 * Public API Surface of sso-auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AuthenticatedSsoGuard, ID_TOKEN_NAME, Interrupt, LOGIN_APP_URL, LOGIN_REALM, LOGIN_SERVICE_URL, LoginResponse, LoginTokens, RoleSsoGuard, SWITCH_USER_CHANGE_MARKET, SsoAuthConstants, SsoAuthInterceptor, SsoAuthModule, SsoAuthService, SsoInterceptExclusionService, SsoLogoutCallbackDefinition, SsoLogoutService, SsoUser };
